import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Sparkles, Gift } from "lucide-react";
import { getWaitlistStats } from "../lib/api";
import Countdown from "./Countdown";

export default function Hero({ onCtaClick, primaryCta, secondaryCta }) {
    const [count, setCount] = useState(null);

    useEffect(() => {
        getWaitlistStats()
            .then((d) => setCount(d.total))
            .catch(() => setCount(null));
    }, []);

    return (
        <section className="relative pt-40 pb-20 md:pt-48 md:pb-28 overflow-hidden">
            {/* Background */}
            <div className="absolute inset-0 -z-10 bg-gradient-to-b from-sky-50/60 via-white to-white" />
            <div className="absolute inset-0 -z-10 bg-grid opacity-50 [mask-image:radial-gradient(ellipse_at_top,white_30%,transparent_70%)]" />
            <div className="absolute -top-24 right-1/4 -z-10 h-72 w-72 rounded-full bg-sky-200/40 blur-3xl" />
            <div className="absolute top-40 left-0 -z-10 h-72 w-72 rounded-full bg-cyan-100/60 blur-3xl" />

            <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                    className="lg:col-span-7"
                >
                    {/* Pre-launch chip */}
                    <div className="flex flex-wrap items-center gap-3 mb-6">
                        <div className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50/70 px-3 py-1 text-xs font-semibold text-sky-700">
                            <span className="relative flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-sky-400 opacity-75" />
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-sky-500" />
                            </span>
                            Pre-launch · 1. oktober 2026
                        </div>
                        <Countdown variant="compact" />
                    </div>

                    <h1
                        data-testid="hero-title"
                        className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05]"
                    >
                        Driftsplatformen{" "}
                        <span className="text-gradient-blue">bygget</span> til danske
                        rengøringsfirmaer.
                    </h1>

                    <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl font-body">
                        Saml køreopgaver, besøg, fakturering, reklamationer og
                        nøgledatabase ét sted. Cleany er den moderne afløser for
                        Excel-ark og papirsedler — skabt til hverdagen i dit
                        rengøringsfirma.
                    </p>

                    <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:items-center">
                        {primaryCta ? (
                            <a
                                href={primaryCta.href}
                                data-testid={primaryCta.testid || "hero-cta-primary"}
                                className="group inline-flex items-center justify-center gap-2 rounded-full bg-sky-600 hover:bg-sky-700 text-white font-medium px-6 h-12 shadow-lg shadow-sky-600/20 transition-all active:scale-[0.97]"
                            >
                                {primaryCta.label}
                                <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                            </a>
                        ) : (
                            <button
                                onClick={onCtaClick}
                                data-testid="hero-cta-waitlist"
                                className="group inline-flex items-center justify-center gap-2 rounded-full bg-sky-600 hover:bg-sky-700 text-white font-medium px-6 h-12 shadow-lg shadow-sky-600/20 transition-all active:scale-[0.97]"
                            >
                                Tilmeld dig launch
                                <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                            </button>
                        )}
                        <a
                            href={secondaryCta?.href || "#moduler"}
                            data-testid={secondaryCta?.testid || "hero-secondary-cta"}
                            className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-300 bg-white hover:border-slate-400 text-slate-800 font-medium px-6 h-12 transition-colors"
                        >
                            {secondaryCta?.label || "Se moduler"}
                        </a>
                    </div>

                    <div className="mt-8 flex flex-wrap items-center gap-6 text-sm text-slate-500">
                        <div className="flex items-center gap-2">
                            <Gift className="h-4 w-4 text-emerald-500" />
                            <strong className="text-slate-700">14 dages gratis prøveperiode</strong>
                        </div>
                        <div className="flex items-center gap-2">
                            <ShieldCheck className="h-4 w-4 text-emerald-500" />
                            GDPR & data hostet i EU
                        </div>
                        <div className="flex items-center gap-2">
                            <Sparkles className="h-4 w-4 text-sky-500" />
                            Ingen binding · Opsig når som helst
                        </div>
                        {count !== null && (
                            <div className="flex items-center gap-2" data-testid="hero-seats-left">
                                <span className="relative flex h-2 w-2">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                                </span>
                                <span>
                                    <strong className="text-emerald-700">{count}</strong>{" "}
                                    tilmeldt så langt
                                </span>
                            </div>
                        )}
                    </div>
                </motion.div>

                {/* Mockup */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 30 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
                    className="lg:col-span-5 relative"
                >
                    <div className="relative animate-float">
                        <div className="absolute -inset-6 bg-gradient-to-tr from-sky-200/50 via-cyan-100/30 to-transparent rounded-[2rem] blur-2xl -z-10" />
                        <div className="rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-sky-900/15 overflow-hidden">
                            <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-slate-200 bg-slate-50">
                                <div className="h-2.5 w-2.5 rounded-full bg-red-300" />
                                <div className="h-2.5 w-2.5 rounded-full bg-yellow-300" />
                                <div className="h-2.5 w-2.5 rounded-full bg-emerald-300" />
                                <div className="ml-3 text-[11px] text-slate-400 font-mono">my.cleany.dk</div>
                            </div>
                            <DashboardMockup />
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}

function DashboardMockup() {
    const navItems = [
        { label: "Dashboard", active: true },
        { label: "Virksomheder" },
        { label: "Personer" },
        { label: "Besøg" },
        { label: "Journal", badge: 65 },
        { label: "Reklamationer", badge: 2 },
        { label: "Timelog" },
        { label: "Fakturering" },
        { label: "Statistikker" },
    ];
    const days = [
        { d: "Søn", v: 7 },
        { d: "Man", v: 9 },
        { d: "Tirs", v: 10 },
        { d: "Ons", v: 9 },
        { d: "Tors", v: 9 },
        { d: "Fre", v: 9 },
        { d: "Lør", v: 7 },
    ];
    const max = Math.max(...days.map((x) => x.v));
    const team = [
        { n: "Anders Iversen", c: "AI Company ApS 1", t: "På tid" },
        { n: "Anders Iversen", c: "AI Company ApS 1", t: "På tid" },
        { n: "Anders Iversen", c: "AI Company ApS 2", t: "Forsinket" },
        { n: "Anders Iversen", c: "AI Company ApS 3", t: "På tid" },
    ];

    return (
        <div className="grid grid-cols-12 bg-slate-50 text-[10px]">
            {/* Sidebar */}
            <div className="col-span-3 bg-white border-r border-slate-200 py-3 px-2.5">
                <div className="flex items-center gap-1 px-1.5 mb-3">
                    <svg viewBox="0 0 24 24" className="h-4 w-4 text-sky-600" fill="currentColor" fillOpacity="0.18" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 22s-7-7.58-7-13a7 7 0 1 1 14 0c0 5.42-7 13-7 13z" />
                        <circle cx="12" cy="9" r="2.5" />
                    </svg>
                    <span className="font-display font-bold text-slate-900 text-[13px]">Cleany</span>
                </div>
                <div className="space-y-0.5">
                    {navItems.map((it, i) => (
                        <div
                            key={it.label}
                            className={`flex items-center justify-between px-2 py-1.5 rounded-md ${
                                it.active ? "bg-sky-50 text-sky-700 font-semibold" : "text-slate-600"
                            }`}
                        >
                            <div className="flex items-center gap-1.5">
                                <span className={`h-1 w-1 rounded-full ${it.active ? "bg-sky-500" : "bg-slate-300"}`} />
                                {it.label}
                            </div>
                            {it.badge && (
                                <span className="bg-sky-100 text-sky-700 rounded-full px-1.5 text-[8px] font-bold">
                                    {it.badge}
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Main */}
            <div className="col-span-9 p-3 space-y-2.5">
                {/* Top stat cards row */}
                <div className="grid grid-cols-4 gap-2">
                    {/* Bar chart card (dark) */}
                    <div className="col-span-2 rounded-lg bg-gradient-to-br from-sky-700 to-sky-900 p-2.5 text-white">
                        <div className="text-[9px] opacity-80 mb-1.5">Ikke udførte opgaver, sidste uge</div>
                        <div className="flex items-end gap-1 h-16">
                            {days.map((d) => (
                                <div key={d.d} className="flex-1 flex flex-col items-center gap-0.5">
                                    <div className="text-[8px] font-bold">{d.v}</div>
                                    <div
                                        className="w-full rounded-t bg-gradient-to-t from-sky-300 to-sky-100"
                                        style={{ height: `${(d.v / max) * 100}%` }}
                                    />
                                    <div className="text-[7px] opacity-70">{d.d}.</div>
                                </div>
                            ))}
                        </div>
                    </div>
                    {/* Stat cards */}
                    {[
                        { v: "50", l: "Antal opgaver i dag" },
                        { v: "84", l: "Medarbejdere på job" },
                    ].map((s) => (
                        <div key={s.l} className="rounded-lg border border-slate-200 bg-white p-2">
                            <div className="font-display font-bold text-sky-700 text-xl leading-none">{s.v}</div>
                            <div className="mt-1 text-[9px] text-slate-600">{s.l}</div>
                        </div>
                    ))}
                </div>

                {/* "På job lige nu" table */}
                <div className="rounded-lg border border-slate-200 bg-white">
                    <div className="px-2.5 py-1.5 border-b border-slate-100 flex items-center justify-between">
                        <div className="text-[10px] font-semibold text-slate-800">På job lige nu (23)</div>
                        <div className="text-[8px] text-slate-400">live</div>
                    </div>
                    <div className="px-2.5 py-1 grid grid-cols-12 gap-2 text-[8px] uppercase tracking-wider text-slate-400 font-semibold border-b border-slate-100">
                        <div className="col-span-4">Medarbejder</div>
                        <div className="col-span-4">Kunde</div>
                        <div className="col-span-3">Status</div>
                        <div className="col-span-1"></div>
                    </div>
                    {team.map((r, i) => (
                        <div
                            key={i}
                            className="px-2.5 py-1.5 grid grid-cols-12 gap-2 items-center border-b border-slate-50 last:border-0"
                        >
                            <div className="col-span-4 flex items-center gap-1.5">
                                <div className="h-4 w-4 rounded-full bg-gradient-to-br from-sky-400 to-sky-600 text-white flex items-center justify-center text-[8px] font-bold">
                                    {r.n.charAt(0)}
                                </div>
                                <span className="text-[9px] text-slate-700 truncate">{r.n}</span>
                            </div>
                            <div className="col-span-4 text-[9px] text-sky-700 underline truncate">{r.c}</div>
                            <div className="col-span-3">
                                <span
                                    className={`inline-flex text-[8px] font-semibold rounded-full px-1.5 py-0.5 ${
                                        r.t === "På tid"
                                            ? "bg-emerald-50 text-emerald-700"
                                            : "bg-amber-50 text-amber-700"
                                    }`}
                                >
                                    {r.t}
                                </span>
                            </div>
                            <div className="col-span-1 text-[9px] text-slate-400">›</div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
